import { IChecklist } from "@/interfaces/CheckLists";
import { Menu, MenuItem, Stack, Typography } from "@mui/material";
import { GridSearchIcon, GridSortDirection } from "@mui/x-data-grid";
import { ConfirmationDialog, ManageChecklistsEvent } from "klayowebshared";
import { debounce } from "lodash";
import { useEffect, useMemo } from "react";
import { track } from "../../common/track";
import { Button } from "../../components/Button";
import { Paper } from "../../components/Paper";
import { CheckListsTable } from "../../components/table/CheckListsTable";
import { TablePlaceholder } from "../../components/TablePlaceholder";
import { TextField } from "../../components/TextField";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useCheckListTable } from "../../hooks/useCheckListTable";
import { useLocale } from "../../providers/LocaleProvider";
import ChecklistIcons from "../../resources/images/checklist.svg";
import AddIcon from "../../resources/images/icons-add.svg";
import { onOpenChecklistPreviewDialog } from "../../store/checklist.slice";

export function SettingsChecklistsView(props: any) {
  const { theme } = props;
  const { translate } = useLocale();
  const {
    search,
    checkLists,
    isLoading,
    checklistsSort,
    checklistsPagination,
    opentAction,
    openDeleteConfirmationDialog,
    isDeleteLoading,
    openPreviewDialog,
    checklistId,
    onChecklistAction,
    onShowCreateChecklistDialog,
    onSearchChecklist,
    onChecklistsPaginationModelChange,
    onChecklistsSortModelChange,
    onEditSelected,
    deleteCheckList,
    openDeleteDialog,
    setOpentAction,
    setOpenPreviewDialog
  } = useCheckListTable();

  const dispatch = useAppDispatch();

  useEffect(() => {
    track(ManageChecklistsEvent.Viewed);
  }, []);

  const trackSearch = useMemo(
    () =>
      debounce((value: string) => {
        if (value) track(ManageChecklistsEvent.Search, { searchtext: value });
      }, 500),
    []
  );

  const checklistColumns = useMemo(
    () => [
      { type: CheckListsTable.columns.lastUpdated, label: translate("tableColumns.lastUpdate") },
      {
        type: CheckListsTable.columns.checklistName,
        label: translate("tableColumns.checklistName")
      },
      { type: CheckListsTable.columns.sections, label: translate("tableColumns.sections") },
      { type: CheckListsTable.columns.questions, label: translate("tableColumns.questions") },
      { type: CheckListsTable.columns.actions, label: translate("tableColumns.actions") }
    ],
    [translate]
  );

  return (
    <div>
      <Paper sx={{ padding: "56px 64px", display: { xs: "none", md: "block" } }}>
        <h1 className='klayo-paper_title'>{translate("settingsViews.checklists.title")}</h1>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          spacing={2}
          sx={{ borderTop: "1px solid rgba(0, 0, 0, 0.12)", padding: "30px 0" }}
        >
          <TextField
            value={search}
            dense={true}
            placeholder={translate("common.search")}
            fullWidth={true}
            autoComplete={false}
            leadingIcon={<GridSearchIcon />}
            sx={{ maxWidth: { md: "300px" } }}
            disabled={checkLists?.data === undefined && !search}
            onChange={(e) => {
              trackSearch(e.target.value);
              onSearchChecklist(e);
            }}
          />

          <div className='klaro_regulations-button-container'>
            <Button
              size='md'
              theme={theme}
              variant='filled'
              label={translate("settingsViews.checklists.newChecklist")}
              onClick={() => {
                track(ManageChecklistsEvent.NewChecklist);
                onShowCreateChecklistDialog();
              }}
              startIcon={<AddIcon />}
            />
          </div>
        </Stack>

        <CheckListsTable<IChecklist>
          theme={theme}
          rows={checkLists?.data}
          columns={checklistColumns}
          onChecklistAction={onChecklistAction}
          isLoading={isLoading}
          paginationMode='server'
          sortingMode='server'
          sortModel={[
            { field: checklistsSort?.field, sort: checklistsSort?.sort as GridSortDirection }
          ]}
          paper={false}
          noResultsComponent={
            <TablePlaceholder
              text={translate("settingsViews.checklists.noChecklistsPlaceholder")}
              icon={<ChecklistIcons />}
            />
          }
          noRowsComponent={
            <TablePlaceholder
              text={
                search === ""
                  ? translate("settingsViews.checklists.noChecklistsPlaceholder")
                  : translate("common.noRows")
              }
              icon={<ChecklistIcons />}
            />
          }
          totalCount={checkLists?.totalCount || 0}
          paginationModel={checklistsPagination}
          onPaginationModelChange={onChecklistsPaginationModelChange}
          onSortModelChange={onChecklistsSortModelChange}
          keyword={search}
          onChecklistClick={(...args: any[]) => {
            track(ManageChecklistsEvent.RowClicked);
            (onEditSelected as any)(...args);
          }}
        />
      </Paper>

      {opentAction && (
        <Menu
          anchorEl={opentAction}
          open={Boolean(opentAction)}
          onClose={() => setOpentAction(null)}
        >
          <MenuItem
            onClick={() => {
              track(ManageChecklistsEvent.MenuOption, { option: "preview" });
              dispatch(
                onOpenChecklistPreviewDialog({
                  id: checklistId as string
                })
              );
              setOpentAction(null);
            }}
          >
            {translate("settingsViews.checklists.preview")}
          </MenuItem>
          <MenuItem
            onClick={() => {
              track(ManageChecklistsEvent.MenuOption, { option: "delete" });
              openDeleteDialog();
              setOpentAction(null);
            }}
          >
            {translate("common.delete")}
          </MenuItem>
        </Menu>
      )}
      {openDeleteConfirmationDialog && (
        <ConfirmationDialog
          theme={theme}
          title={translate("settingsViews.checklists.deleteChecklist")}
          question={<div>{translate("settingsViews.checklists.deleteChecklistConfirm")}</div>}
          acceptButton={translate("settingsViews.checklists.deleteChecklist")}
          cancelButton={translate("common.cancel")}
          acceptDanger={true}
          loading={isDeleteLoading}
          error={null}
          onCancel={() => openDeleteDialog()}
          onAccept={() => deleteCheckList()}
        />
      )}
      <Typography
        sx={{
          display: { xs: "block", md: "none" },
          textAlign: "center",
          marginTop: "100px",
          fontFamily: "Inter",
          fontSize: "20px",
          color: "red",
          p: 10
        }}
      >
        {translate("settingsViews.checklists.pageNotAvailable")}
      </Typography>
    </div>
  );
}
